import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';

import { SKIP_AUTH_CONTEXT_KEY, CLIENT_FEATURE_FLAGS } from '@/constants';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import { isValidZipCode } from '@/utilities/globalValidations';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { useSeekerState, useAppDispatch } from '@/components/AppState';
import { GET_ZIP_CODE_SUMMARY } from '@/components/request/GQL';

import { TrafficSource } from '@/types/seeker';

import { getZipcodeSummary, getZipcodeSummaryVariables } from '@/__generated__/getZipcodeSummary';
import { YesOrNoAnswer } from '@/__generated__/globalTypes';

export const useHideEnrollmentSteps = () => {
  const [isUserEligible, setIsUserEligible] = useState<boolean | undefined>(false);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { zipcode, source } = useSeekerState();
  const { asPath } = router;
  const {
    fromSeoPage,
    fromSemPage,
    zipcode: queryParamZip,
    serviceIdForMember,
    careKind,
  } = router.query;

  const skipSEOStepsTestEvaluation = useFeatureFlag(CLIENT_FEATURE_FLAGS.SKIP_STEPS_SEO);
  const skipSEMStepsTestEvaluation = useFeatureFlag(CLIENT_FEATURE_FLAGS.SKIP_STEPS_SEM);
  const isEvaluatedToSkipSEOSteps = skipSEOStepsTestEvaluation?.value === 2;
  const isEvaluatedToSkipSEMSteps = skipSEMStepsTestEvaluation?.value === 2;
  const fromSeoPageCalculated = source === TrafficSource.SEO || fromSeoPage === 'true';
  const fromSemPageCalculated = source === TrafficSource.SEM || fromSemPage === 'true';

  const isSeekerFlow = asPath.includes('seeker');

  const handleCompletedZipCodeQuery = (response: getZipcodeSummary) => {
    const { getZipcodeSummary: getZipcodeSummaryData } = response;
    const inputtedZipUnchangedFromQueryParam = zipcode === queryParamZip;
    setIsUserEligible(
      fromSeoPageCalculated &&
        isSeekerFlow &&
        getZipcodeSummaryData.__typename === 'ZipcodeSummary' &&
        inputtedZipUnchangedFromQueryParam
    );
    if (getZipcodeSummaryData.__typename === 'ZipcodeSummary') {
      dispatch({
        type: 'setCityAndState',
        city: getZipcodeSummaryData.city,
        state: getZipcodeSummaryData.state,
      });
    }
  };

  const zipNotOnQueryParam = queryParamZip === '' || typeof queryParamZip === 'undefined';

  const handleErrorZipCodeQuery = () => {
    setIsUserEligible(fromSeoPageCalculated && isSeekerFlow);
  };

  const [getZipCodeSummary] = useLazyQuery<getZipcodeSummary, getZipcodeSummaryVariables>(
    GET_ZIP_CODE_SUMMARY,
    {
      context: { [SKIP_AUTH_CONTEXT_KEY]: true },
      onCompleted: handleCompletedZipCodeQuery,
      onError: handleErrorZipCodeQuery,
    }
  );

  useEffect(() => {
    if (isValidZipCode(zipcode)) {
      getZipCodeSummary({
        variables: {
          zipcode,
        },
      });
    } else if (zipNotOnQueryParam) {
      const isDaycareSubVertical = serviceIdForMember === 'dayCare';
      const baseEligibility = (fromSeoPageCalculated || fromSemPageCalculated) && isSeekerFlow;

      if (isDaycareSubVertical) {
        const careKindNotOnQueryParam = careKind === '' || typeof careKind === 'undefined';
        setIsUserEligible(baseEligibility && careKindNotOnQueryParam);
      } else {
        setIsUserEligible(baseEligibility);
      }
    }
  }, [zipcode, fromSeoPageCalculated, fromSemPageCalculated]);

  useEffect(() => {
    if (isUserEligible) {
      if (fromSeoPageCalculated) {
        AnalyticsHelper.logTestExposure(
          CLIENT_FEATURE_FLAGS.SKIP_STEPS_SEO,
          skipSEOStepsTestEvaluation
        );
      } else if (fromSemPageCalculated) {
        AnalyticsHelper.logTestExposure(
          CLIENT_FEATURE_FLAGS.SKIP_STEPS_SEM,
          skipSEMStepsTestEvaluation
        );
      }

      const isAssignedToSkipSteps =
        (fromSeoPageCalculated && isEvaluatedToSkipSEOSteps) ||
        (fromSemPageCalculated && isEvaluatedToSkipSEMSteps);

      dispatch({
        type: 'setIsUserAssignedToSkipSteps',
        isAssigned: isAssignedToSkipSteps ? YesOrNoAnswer.YES : YesOrNoAnswer.NO,
      });
    }
  }, [isUserEligible]);

  return { isUserEligibleForTest: isUserEligible };
};
